import { useCallback, useMemo } from 'react'
import {
  BayGap,
  CONTAINER_DETAILS_WIDTH,
  ContainerViewMode,
  buildBayGaps,
  edgeAt,
} from '../helpers/yard-view.utils'
import { YardViewStore } from '../stores/yard.view-store'

export interface DetailsGridRails {
  /** Gap on the leading (left) edge of the visible columns. */
  leading?: BayGap
  /** Gap on the trailing (right) edge of the visible columns. */
  trailing?: BayGap
  /**
   * Inter-column gaps keyed by 1-based data-column index. In ROW mode the keys
   * are bay numbers; in BAY mode they are row indices in the visible cut.
   */
  inner: Map<number, BayGap>
}

/**
 * Derives bay-axis gap layout for the details grid and footer (BAY/ROW modes).
 *
 * NOTE: `columnLength` is assumed to equal the block's maxBays (ROW mode) or
 * maxRows (BAY mode). The details drawer always renders the full range — there
 * is no partial-bay view.
 */
export const useDetailsGridRails = (viewStore: YardViewStore, columnLength: number) => {
  const rails = useMemo<DetailsGridRails>(() => {
    const block = viewStore.focusedYardBlock
    if (!block) return { inner: new Map() }

    const bayGaps = buildBayGaps(block.configAreas, block.maxBays)

    if (viewStore.containerViewMode === ContainerViewMode.BAY && viewStore.selectedBay) {
      // BAY mode: a structure on either edge of the selected bay extends across all
      // row gaps in the vertical cut. Project the leading/trailing gaps onto a per-row
      // map (indexed 1..columnLength-1).
      const selected = viewStore.selectedBay
      const leading = edgeAt(bayGaps, selected - 1)
      const trailing = edgeAt(bayGaps, selected)
      const inner = new Map<number, BayGap>()

      // Choose the rail that visually represents both edges in row gaps when both exist.
      // Prefer the wider; if equal width, prefer trailing (arbitrary tie-break).
      const rowGapPrototype = !leading
        ? trailing
        : !trailing
          ? leading
          : trailing.widthPx >= leading.widthPx
            ? trailing
            : leading

      if (rowGapPrototype) {
        for (let row = 1; row < columnLength; row++) {
          inner.set(row, rowGapPrototype)
        }
      }

      return { leading, trailing, inner }
    }

    if (viewStore.containerViewMode === ContainerViewMode.ROW) {
      // ROW mode: the visible columns are bays 1..columnLength. Inter-bay gaps are
      // edges 1..columnLength-1 from the producer. A leading gap at edge 0 surfaces
      // separately so the consumer can render it in a leading column.
      const inner = new Map<number, BayGap>()
      bayGaps.forEach(g => {
        if (g.edge >= 1 && g.edge <= columnLength - 1) {
          inner.set(g.edge, g)
        }
      })
      return {
        leading: edgeAt(bayGaps, 0),
        // Trailing edge (`edge = maxBays`) is intentionally excluded — that gap is the
        // trailing aft of the LAST bay, which falls outside the visible bay range.
        trailing: undefined,
        inner,
      }
    }

    return { inner: new Map() }
  }, [viewStore.containerViewMode, viewStore.selectedBay, viewStore.focusedYardBlock, columnLength])

  const totalCssColumns = useMemo(() => {
    let count = columnLength
    if (rails.leading) count++
    if (rails.trailing) count++
    count += rails.inner.size
    return count
  }, [rails, columnLength])

  const gridTemplateColumns = useMemo(() => {
    const parts: string[] = []
    if (rails.leading) parts.push(`${rails.leading.widthPx}px`)
    for (let c = 1; c <= columnLength; c++) {
      parts.push(`${CONTAINER_DETAILS_WIDTH}px`)
      const inner = rails.inner.get(c)
      if (inner) parts.push(`${inner.widthPx}px`)
    }
    if (rails.trailing) parts.push(`${rails.trailing.widthPx}px`)
    return parts.join(' ')
  }, [rails, columnLength])

  const hasAnyRail = !!rails.leading || !!rails.trailing || rails.inner.size > 0

  /** CSS column index (1-based) for a data column (1-based). */
  const cssColumnForDataIndex = useCallback(
    (dataIdx: number): number => {
      let col = rails.leading ? 2 : 1
      for (let c = 1; c < dataIdx; c++) {
        col++
        if (rails.inner.has(c)) col++
      }
      return col
    },
    [rails],
  )

  /** Extra CSS columns a span of N data columns covers due to gap columns between them. */
  const extraGapColumnsInSpan = useCallback(
    (startDataIdx: number, span: number): number => {
      let extra = 0
      for (let c = startDataIdx; c < startDataIdx + span - 1; c++) {
        if (rails.inner.has(c)) extra++
      }
      return extra
    },
    [rails],
  )

  return {
    rails,
    totalCssColumns,
    gridTemplateColumns,
    hasAnyRail,
    cssColumnForDataIndex,
    extraGapColumnsInSpan,
  }
}
